import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Home,
  Server,
  HardDrive,
  Database,
  Activity,
  FileText,
  Network,
  ShoppingBag,
  CreditCard,
  Globe,
} from "lucide-react";
import { cn } from "@/lib/utils";
import logoLight from "@/assets/logo-light.svg";
import logoDark from "@/assets/logo-dark.svg";
import { ScrollArea } from "@/components/ui/scroll-area";
import { SidebarCollapseButton } from "./SidebarCollapseButton";
import {
  useDomainRegistrationEnabled,
  useDatabaseManagementEnabled,
  useObjectStorageEnabled,
  useFirewallEnabled,
  useMonitoringEnabled,
} from "@/hooks/useFeatureFlags";

const SIDEBAR_COLLAPSED_KEY = "wicloud_sidebar_collapsed";

export interface NavItemDef {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  path?: string;
}

export interface NavGroup {
  titleKey:
    | "sidebar.navigation.overview"
    | "sidebar.navigation.compute"
    | "sidebar.navigation.data"
    | "sidebar.navigation.network"
    | "sidebar.navigation.domainsGroup"
    | "sidebar.navigation.commerce"
    | "sidebar.navigation.observability";
  items: NavItemDef[];
}

// TODO: move non-component exports (useNavigationGroups, isNavPathActive) to a separate sidebar utils file
// eslint-disable-next-line react-refresh/only-export-components
export const useNavigationGroups = (): NavGroup[] => {
  const { t } = useTranslation("common");
  const domainsEnabled = useDomainRegistrationEnabled();
  const databasesEnabled = useDatabaseManagementEnabled();
  const objectStorageEnabled = useObjectStorageEnabled();
  const firewallEnabled = useFirewallEnabled();
  const monitoringEnabled = useMonitoringEnabled();

  // These are UI-level feature gates configured by an admin in Platform
  // Settings → Feature Flags. Keep the conditional items below: a disabled
  // capability must not be advertised in the client navigation.
  const dataItems: NavItemDef[] = [
    ...(databasesEnabled
      ? [
          {
            label: t("sidebar.navigation.databases"),
            icon: Database,
            path: "/resources/databases",
          },
        ]
      : []),
    ...(objectStorageEnabled
      ? [
          {
            label: t("sidebar.navigation.objectStorage"),
            icon: HardDrive,
            path: "/resources/storage",
          },
        ]
      : []),
  ];

  const groups: NavGroup[] = [
    {
      titleKey: "sidebar.navigation.overview",
      items: [
        {
          label: t("sidebar.navigation.dashboard"),
          icon: Home,
          path: "/dashboard",
        },
      ],
    },
    {
      titleKey: "sidebar.navigation.compute",
      items: [
        {
          label: t("sidebar.navigation.virtualMachines"),
          icon: Server,
          path: "/resources/vms",
        },
      ],
    },
    ...(dataItems.length > 0
      ? [
          {
            titleKey: "sidebar.navigation.data" as const,
            items: dataItems,
          },
        ]
      : []),
    // FirewallEnabled gates only this sidebar entry point; the firewall
    // section inside a VM's Network tab (ResourceNetworkTab) always renders.
    ...(firewallEnabled
      ? [
          {
            titleKey: "sidebar.navigation.network" as const,
            items: [
              {
                label: t("sidebar.navigation.firewall"),
                icon: Network,
                path: "/resources/vpc",
              },
            ],
          },
        ]
      : []),
    // Domains group — entire group omitted when the domain-registration flag is off
    ...(domainsEnabled
      ? [
          {
            titleKey: "sidebar.navigation.domainsGroup" as const,
            items: [
              {
                label: t("sidebar.navigation.domainsManage"),
                icon: Globe,
                path: "/domains/manage",
              },
              {
                label: t("sidebar.navigation.domains"),
                icon: Globe,
                path: "/domains/search",
              },
              {
                label: t("sidebar.navigation.domainsTransfer"),
                icon: Globe,
                path: "/domains/transfer-in",
              },
            ],
          },
        ]
      : []),
    {
      titleKey: "sidebar.navigation.commerce",
      items: [
        {
          label: t("sidebar.navigation.orders"),
          icon: ShoppingBag,
          path: "/orders",
        },
        {
          label: t("sidebar.navigation.billing"),
          icon: CreditCard,
          path: "/billing",
        },
      ],
    },
    {
      titleKey: "sidebar.navigation.observability",
      items: [
        // Monitoring is intentionally optional. Logs stay visible when it is
        // disabled because they are a separate client capability.
        ...(monitoringEnabled
          ? [
              {
                label: t("sidebar.navigation.monitoring"),
                icon: Activity,
                path: "/monitoring",
              },
            ]
          : []),
        {
          label: t("sidebar.navigation.logs"),
          icon: FileText,
          path: "/logs",
        },
      ],
    },
  ];

  return groups;
};

// eslint-disable-next-line react-refresh/only-export-components
export function isNavPathActive(pathname: string, itemPath: string): boolean {
  if (pathname === itemPath) return true;
  return pathname.startsWith(`${itemPath}/`);
}

interface SidebarProps {
  className?: string;
}

export function Sidebar({ className }: SidebarProps) {
  const { t } = useTranslation("common");
  const navGroups = useNavigationGroups();
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const stored = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    return stored === "true";
  });

  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(isCollapsed));
    window.dispatchEvent(
      new CustomEvent("sidebar-toggle", { detail: { collapsed: isCollapsed } }),
    );
  }, [isCollapsed]);

  const handleToggle = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <aside
      className={cn(
        "fixed left-0 top-0 z-30 h-screen border-r border-hairline bg-panel flex flex-col",
        isCollapsed ? "w-16" : "w-[248px]",
        className,
      )}
    >
      {/* Logo Section */}
      <Link
        to="/dashboard"
        className={cn(
          "flex h-16 items-center border-b border-hairline px-4 bg-panel",
          isCollapsed ? "justify-center px-2" : "justify-start",
        )}
      >
        <div className="flex items-center gap-3">
          <img
            src={logoDark}
            alt="WiServices"
            className="h-6 block dark:block hidden"
          />
          <img
            src={logoLight}
            alt="WiServices"
            className="h-6 block dark:hidden"
          />
          {!isCollapsed && (
            <span className="text-sm font-mono text-muted tracking-wide">
              Console
            </span>
          )}
        </div>
      </Link>

      {/* Navigation Menu */}
      <ScrollArea className="flex-1 px-3 py-2">
        <nav className="flex flex-col gap-0.5">
          {navGroups.map((group) => (
            <div key={group.titleKey} className="mb-3">
              {!isCollapsed && (
                <div className="px-2.5 py-2 text-[10.5px] font-medium tracking-[0.12em] uppercase text-muted-2">
                  {t(group.titleKey)}
                </div>
              )}
              {group.items.map((item) => (
                <SidebarNavItem
                  key={item.path}
                  item={item}
                  isCollapsed={isCollapsed}
                />
              ))}
            </div>
          ))}
        </nav>
      </ScrollArea>

      {/* Collapse Button */}
      <SidebarCollapseButton
        isCollapsed={isCollapsed}
        onToggle={handleToggle}
      />
    </aside>
  );
}

interface SidebarNavItemProps {
  item: NavItemDef;
  isCollapsed: boolean;
}

function SidebarNavItem({ item, isCollapsed }: SidebarNavItemProps) {
  const location = useLocation();
  const isActive = item.path
    ? isNavPathActive(location.pathname, item.path)
    : false;

  const content = (
    <div
      className={cn(
        "flex items-center gap-2.5 w-full px-2.5 py-[7px] rounded-md text-sm transition-colors",
        isActive
          ? "bg-brand-soft text-brand-hover"
          : "text-ink-2 hover:bg-panel-2 hover:text-ink",
        isCollapsed && "justify-center px-0",
      )}
    >
      <item.icon
        className={cn(
          "h-[15px] w-[15px] flex-shrink-0",
          isActive ? "text-brand-hover" : "text-muted",
        )}
      />
      {!isCollapsed && <span>{item.label}</span>}
    </div>
  );

  if (!item.path) return <div>{content}</div>;

  return <Link to={item.path}>{content}</Link>;
}
